import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, ArrowRight, ArrowLeft, Star, Loader2 } from "lucide-react";
import { api, fmt, errMsg, eventTypes, resolveImg, fmtRange } from "@/lib/api";
import Calendar from "@/components/Calendar";

const STEPS = ["الباقة", "الإضافات", "التاريخ", "بياناتك", "الشروط"];

export default function Booking() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [venueId, setVenueId] = useState(null);
  const [pkg, setPkg] = useState(null);
  const [addons, setAddons] = useState([]);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState({ start: "18:00", end: "22:00" });
  const [form, setForm] = useState({ name: "", phone: "", event_type: "عرس", guests: "", notes: "" });
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { data: venues } = useQuery({ queryKey: ["pub-venues"], queryFn: async () => (await api.get("/public/venues")).data });
  useEffect(() => { if (venues?.length && !venueId) setVenueId(venues[0].id); }, [venues, venueId]);

  const { data: detail } = useQuery({
    queryKey: ["pub-venue", venueId],
    queryFn: async () => (await api.get(`/public/venues/${venueId}`)).data,
    enabled: !!venueId,
  });
  const { data: terms } = useQuery({ queryKey: ["pub-terms"], queryFn: async () => (await api.get("/public/terms")).data });

  useEffect(() => {
    const slots = (detail?.venue?.time_slots || []).filter((s) => s.active !== false);
    if (slots.length) setTime({ start: slots[0].start, end: slots[0].end });
  }, [detail]);

  const total = useMemo(() => (pkg?.price || 0) + addons.reduce((s, a) => s + a.price, 0), [pkg, addons]);

  const toggleAddon = (a) => setAddons((p) => p.find((x) => x.id === a.id) ? p.filter((x) => x.id !== a.id) : [...p, a]);

  const canNext = () => {
    if (step === 0) return !!pkg;
    if (step === 2) return !!date;
    if (step === 3) return form.name.trim() && form.phone.trim() && form.guests;
    if (step === 4) return agreed;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/bookings", {
        venue_id: venueId, package_id: pkg.id, addon_ids: addons.map((a) => a.id),
        date, start_time: time.start, end_time: time.end,
        name: form.name, phone: form.phone, event_type: form.event_type,
        guests: Number(form.guests), notes: form.notes, terms_agreed: agreed,
      });
      navigate(`/status/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen gradient-navy text-offwhite pb-32">
      {/* Header */}
      <div className="glass sticky top-0 z-20 px-5 py-4 flex items-center justify-between">
        <button onClick={() => step === 0 ? navigate("/") : setStep(step - 1)} data-testid="booking-back" className="text-gold flex items-center gap-1 text-sm">
          <ArrowRight size={18} /> رجوع
        </button>
        <span className="font-display text-lg gold-text">{detail?.venue?.name || "قمرية"}</span>
        <span className="text-xs text-muted2">{step + 1}/{STEPS.length}</span>
      </div>

      {/* Progress */}
      <div className="max-w-3xl mx-auto px-5 pt-6">
        <div className="flex items-center gap-1.5 mb-8">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1.5 rounded-full transition-colors ${i <= step ? "bg-gold" : "bg-navy3"}`} />
              <p className={`text-[10px] mt-1.5 text-center ${i === step ? "text-gold" : "text-muted2"}`}>{s}</p>
            </div>
          ))}
        </div>

        {/* Step 0: Packages */}
        {step === 0 && (
          <div className="space-y-4 animate-fade-up" data-testid="step-packages">
            <h2 className="font-display text-2xl mb-2">اختر باقتك</h2>
            {(detail?.packages || []).map((p, idx) => {
              const active = pkg?.id === p.id;
              const featured = idx === 1;
              return (
                <div key={p.id} data-testid={`package-${p.id}`} onClick={() => setPkg(p)}
                  className={`card-lux p-5 cursor-pointer transition-colors relative ${active ? "ring-2 ring-gold" : "hover:bg-navy3/40"} ${featured ? "border-gold/40" : ""}`}>
                  {featured && <span className="absolute -top-3 start-5 bg-gold text-navy text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1"><Star size={12} /> الأكثر طلبًا</span>}
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-display text-xl text-offwhite">{p.name}</h3>
                    <span className="gold-text font-bold text-lg">{fmt(p.price)}</span>
                  </div>
                  {p.description && <p className="text-muted2 text-sm mb-3">{p.description}</p>}
                  <ul className="space-y-1.5">
                    {p.services.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-offwhite/90">
                        <Check size={16} className="text-gold mt-0.5 shrink-0" /> {s}
                      </li>
                    ))}
                  </ul>
                  <div className={`mt-4 text-center py-2.5 rounded-full text-sm font-semibold ${active ? "btn-gold" : "border border-gold/40 text-gold"}`}>
                    {active ? "✓ تم الاختيار" : "اختيار الباقة"}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Step 1: Addons */}
        {step === 1 && (
          <div className="space-y-4 animate-fade-up" data-testid="step-addons">
            <h2 className="font-display text-2xl mb-2">إضافات اختيارية</h2>
            {(detail?.addons || []).map((a) => {
              const active = !!addons.find((x) => x.id === a.id);
              return (
                <label key={a.id} data-testid={`addon-${a.id}`}
                  className={`card-lux p-4 flex items-center gap-4 cursor-pointer transition-colors ${active ? "ring-1 ring-gold" : "hover:bg-navy3/40"}`}>
                  <input type="checkbox" checked={active} onChange={() => toggleAddon(a)} className="hidden" />
                  <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 ${active ? "bg-gold border-gold" : "border-muted2"}`}>
                    {active && <Check size={16} className="text-navy" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-offwhite font-medium">{a.name}</p>
                    {a.description && <p className="text-muted2 text-xs">{a.description}</p>}
                  </div>
                  <span className="gold-text font-semibold text-sm">+ {fmt(a.price)}</span>
                </label>
              );
            })}
            {!detail?.addons?.length && <p className="text-muted2 text-center">لا توجد إضافات.</p>}
          </div>
        )}

        {/* Step 2: Calendar */}
        {step === 2 && (
          <div className="animate-fade-up" data-testid="step-calendar">
            <h2 className="font-display text-2xl mb-4">اختر التاريخ</h2>
            <Calendar venueId={venueId} selectedDate={date} onSelect={setDate} />
            {date && <p className="text-center mt-4 text-gold">التاريخ المختار: {date}</p>}
            <div className="mt-5" data-testid="slot-picker">
              <label className="text-sm text-muted2 mb-2 block">اختر وقت الحفلة</label>
              {(() => {
                const slots = (detail?.venue?.time_slots || []).filter((s) => s.active !== false);
                if (!slots.length) return <p className="text-xs text-muted2">وقت الحفلة: {fmtRange(time.start, time.end)}</p>;
                return (
                  <div className="grid grid-cols-1 gap-2">
                    {slots.map((s, i) => {
                      const on = time.start === s.start && time.end === s.end;
                      return (
                        <button key={i} data-testid={`slot-${i}`} onClick={() => setTime({ start: s.start, end: s.end })}
                          className={`py-3 rounded-xl text-sm transition-colors ${on ? "btn-gold" : "card-lux text-offwhite hover:bg-navy3/40"}`}>
                          {fmtRange(s.start, s.end)}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Step 3: Form */}
        {step === 3 && (
          <div className="space-y-4 animate-fade-up" data-testid="step-form">
            <h2 className="font-display text-2xl mb-2">بياناتك</h2>
            <Field label="الاسم الكامل" testid="input-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <Field label="رقم الهاتف" testid="input-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} type="tel" />
            <div>
              <label className="text-sm text-muted2 mb-1.5 block">نوع المناسبة</label>
              <div className="grid grid-cols-2 gap-2">
                {eventTypes.map((t) => (
                  <button key={t} data-testid={`event-${t}`} onClick={() => setForm({ ...form, event_type: t })}
                    className={`py-3 rounded-xl text-sm transition-colors ${form.event_type === t ? "btn-gold" : "card-lux text-offwhite hover:bg-navy3/40"}`}>{t}</button>
                ))}
              </div>
            </div>
            <Field label="عدد الضيوف التقريبي" testid="input-guests" value={form.guests} onChange={(v) => setForm({ ...form, guests: v.replace(/[^0-9]/g, "") })} type="tel" />
            <div>
              <label className="text-sm text-muted2 mb-1.5 block">ملاحظات (اختياري)</label>
              <textarea data-testid="input-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3} className="w-full card-lux p-3 bg-navy2 text-offwhite outline-none focus:ring-1 focus:ring-gold resize-none" />
            </div>
          </div>
        )}

        {/* Step 4: Terms */}
        {step === 4 && (
          <div className="animate-fade-up" data-testid="step-terms">
            <h2 className="font-display text-2xl mb-4">شروط الحجز في قمرية</h2>
            <div className="card-lux p-5 space-y-4 mb-5 max-h-[45vh] overflow-y-auto">
              {(terms || []).map((t, i) => (
                <div key={t.id} className="flex gap-3">
                  <span className="w-7 h-7 rounded-full bg-gold/15 text-gold flex items-center justify-center text-sm font-bold shrink-0">{i + 1}</span>
                  <p className="text-sm text-offwhite/90 leading-relaxed">{t.text}</p>
                </div>
              ))}
            </div>
            <label data-testid="agree-checkbox" className="flex items-center gap-3 cursor-pointer card-lux p-4">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="hidden" />
              <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 ${agreed ? "bg-gold border-gold" : "border-muted2"}`}>
                {agreed && <Check size={16} className="text-navy" />}
              </div>
              <span className="text-sm text-offwhite">لقد قرأت شروط الحجز وأنا موافق عليها.</span>
            </label>
          </div>
        )}
      </div>

      {/* Bottom bar */}
      <div className="fixed bottom-0 inset-x-0 glass px-5 py-4 z-20">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
          <div>
            <p className="text-xs text-muted2">المجموع التقديري</p>
            <p className="gold-text font-bold text-lg" data-testid="running-total">{fmt(total)}</p>
          </div>
          {step < STEPS.length - 1 ? (
            <button data-testid="next-btn" disabled={!canNext()} onClick={() => canNext() && setStep(step + 1)}
              className="btn-gold px-8 py-3 rounded-full flex items-center gap-2 disabled:opacity-40">
              متابعة <ArrowLeft size={18} />
            </button>
          ) : (
            <button data-testid="submit-booking-btn" disabled={!agreed || submitting} onClick={submit}
              className="btn-gold px-8 py-3 rounded-full flex items-center gap-2 disabled:opacity-40">
              {submitting ? <Loader2 className="animate-spin" size={18} /> : null} إرسال الطلب
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-sm text-muted2 mb-1.5 block">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full card-lux p-3 bg-navy2 text-offwhite outline-none focus:ring-1 focus:ring-gold" />
    </div>
  );
}
